import { api, endpoint } from "@/api";
import appConfig from "@/config/appConfig";
import { userRole } from "@/constant";
import { useSearchQuery } from "@/utils/useSearchQuery";
import { flare } from "@flareapp/js";
import { Button, Empty } from "antd";
import axios from "axios";
import * as htmlToImage from "html-to-image";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { BiMessageSquareEdit } from "react-icons/bi";
import { FaPlus } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { v4 as uuidV4 } from "uuid";
import ReportLayout from "../ReportLayout";
import CustomReportAddItemModal from "./CustomReportAddItemModal";
import CustomReportComponentModal from "./CustomReportComponentModal";
import MetaCustomReportModal from "./MetaCustomReportModal";

const CustomReportBody = ({ mode, id, layouts, setLayouts, gridData = [], setGridData, selectedTiles = [], handleSelect = () => {} }) => {
  const { userDetails } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const cp = useSearchQuery("cp") || undefined;
  const ref = useRef(null);

  const takeScreenshot = async () => {
    try {
      const node = document.getElementById("my-node");
      const dataUrl = await htmlToImage.toPng(node);

      return dataUrl;
    } catch (error) {
      flare.report(error);
      console.log("Error taking screenshot:", error);
    }
  };

  // state
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  // modal
  const [updateReportModal, setUpdateReportModal] = useState(false);
  const [componentModal, setComponentModal] = useState(false);
  const [addItemModal, setAddItemModal] = useState(false);
  const [editItem, setEditItem] = useState({});
  const [iframe, setIframe] = useState();

  const getData = async () => {
    try {
      if (!isLoading) {
        const url = mode === "create" ? endpoint.customReportTemplate : endpoint.customReport;
        setIsLoading(true);
        const { result } = await api.get(url + "/" + id);
        if (result) {
          if (cp === "true") {
            result.name = `${result.name} (copy)`;
          }
          setData(result);
          setLayouts(result?.layouts);
          setGridData(result?.items || []);
        }
      }
    } catch (error) {
      flare.report(error);
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (body) => {
    try {
      const response = await axios.put(endpoint.update_customReport + "/" + data._id, body, { withCredentials: true });
      // console.log({ response });
      if (response.status === 204) {
        Swal.fire("", "Report Updated Successfully!", "success");
        navigate("/report-list");
      }
    } catch (error) {
      flare.report(error);
      console.log(error);
    }
  };

  const handleCreate = async (body) => {
    try {
      const response = await axios.post(endpoint.create_customReport, body, { withCredentials: true });

      if (response.status === 204) {
        Swal.fire("", "Report Created Successfully!", "success");
        navigate("/report-list");
      }
    } catch (error) {
      flare.report(error);
      console.log(error);
    }
  };

  const handleSubmit = async () => {
    try {
      // Wait for charts to render
      // await new Promise((resolve) => setTimeout(resolve, 1000)); // 1 second delay
      const image = await takeScreenshot(ref.current);
      // eslint-disable-next-line no-unused-vars
      const { _id, createdAt, updatedAt, ...rest } = data;
      const body = { ...rest, layouts, items: gridData, image };

      // generate
      if (cp === "true" || mode === "create") {
        body.reportType = uuidV4();
        await handleCreate(body);
        Swal.fire(
          mode === "create" ? "Generated!" : "Duplicated!",
          `Report ${mode === "create" ? "Generate" : "Duplicated"} Successfully.`,
          "success",
        );
      } else {
        await handleUpdate(body);
        Swal.fire("Updated!", "Report has been Updated.", "success");
      }
      navigate("/report-list");
    } catch {
      Swal.fire("", "Error Happened", "error");
    }
  };

  useEffect(() => {
    if (mode !== "view") {
      setIsEditable(true);
    }
  }, [mode]);

  useEffect(() => {
    getData();
  }, [id]);

  useEffect(() => {
    document.title = `${appConfig.name} - Report`;
  }, []);

  return (
    <>
      <div>
        <div className="d-md-flex align-items-center justify-content-between">
          <div>
            <label className="az-content-label">Manage Report</label>
            <span className="d-block py-2">
              {mode === "view" ? "View" : "Generate"} Report: {data?.name}
            </span>
          </div>

          {/* edit options */}

          {![userRole.public, userRole.user].includes(userDetails?.role) && mode !== "view" && (
            <div className="d-flex flex-column flex-md-row gap-2 align-items-md-center align-items-start ms-auto mb-3">
              {isEditable && (
                <>
                  <Button
                    className="bg-secondary-subtle text-primary fw-medium"
                    type="primary"
                    icon={<BiMessageSquareEdit />}
                    onClick={() => setUpdateReportModal((pre) => !pre)}
                  >
                    Edit Report
                  </Button>
                  {mode === "create" && (
                    <Button type="primary" className="fw-medium" onClick={() => setComponentModal((pre) => !pre)} icon={<FaPlus />}>
                      Add Items
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <hr className="mt-0 mb-4" />
        {/* body */}
        {gridData?.length > 0 ? (
          <div className="pb-5 px-3">
            <div className="bg-layout">
              <div ref={ref} id="my-node">
                <ReportLayout
                  {...{
                    gridData,
                    setGridData,
                    layouts,
                    setLayouts,
                    mode: isEditable ? "create" : "view",
                    setComponentModal,
                    setEditItem,
                    selectedTiles,
                    handleSelect,
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="m-auto py-5">
            <Empty />
          </div>
        )}

        {/* buttons */}
        {!["home", "view"].includes(mode) && (
          <div className="d-flex align-items-center justify-content-end gap-2 pt-2">
            <Button type="primary" onClick={handleSubmit}>
              {cp === "true" ? "Create" : mode === "edit" ? "Update" : "Generate"}
            </Button>
            <Button type="primary" className="bg-secondary" onClick={() => navigate(-1)} htmlType="button">
              Cancel
            </Button>
          </div>
        )}
      </div>

      {updateReportModal && (
        <MetaCustomReportModal
          {...{
            show: updateReportModal,
            setShow: setUpdateReportModal,
            mode: cp === "true" ? "copy" : "edit",
            data,
            setData,
            setGridData,
            setLayouts,
            getData: getData,
          }}
        />
      )}
      {componentModal && (
        <CustomReportComponentModal
          {...{
            show: componentModal,
            setShow: setComponentModal,
            mode: "create",
            editItem,
            setEditItem,
            setAddItemModal,
            setGridData,
            gridData,
            data,
            iframe,
          }}
        />
      )}
      {addItemModal && (
        <CustomReportAddItemModal
          {...{
            show: addItemModal,
            setShow: setAddItemModal,
            mode: gridData.find((item) => item._id === editItem._id) ? "edit" : "create",
            editItem,
            setEditItem,
            data,
            gridData,
            setGridData,
            setData,
            iframe,
            setIframe,
            setComponentModal,
          }}
        />
      )}
    </>
  );
};

export default CustomReportBody;

CustomReportBody.propTypes = {
  mode: PropTypes.string,
  id: PropTypes.string,
  layouts: PropTypes.array,
  setLayouts: PropTypes.func,
  gridData: PropTypes.array,
  setGridData: PropTypes.func,
  selectedTiles: PropTypes.array,
  handleSelect: PropTypes.func,
};
